import { type PropsWithChildren, useEffect, useMemo } from 'react';

import { notification } from 'antd';

import { useSessionIsAuthorized } from '@/entities/auth';

import { AntNotificationContext } from '@/shared/lib/contexts';
import {
    SHOW_API_ERROR_EVENT,
    type ShowApiErrorEvent,
} from '@/shared/lib/store';

interface AntNotificationProviderProps extends PropsWithChildren {}

export function AntNotificationProvider(props: AntNotificationProviderProps) {
    const { children } = props;

    const isAuthorized = useSessionIsAuthorized();

    const [staticInstance, contextHolder] = notification.useNotification({
        prefixCls: isAuthorized ? '' : 'auth-notify',
    });

    useEffect(() => {
        const showErrorListener = ((event: ShowApiErrorEvent) => {
            staticInstance.error(event.detail);
        }) as EventListener;

        document.addEventListener(SHOW_API_ERROR_EVENT, showErrorListener);

        return () =>
            document.removeEventListener(
                SHOW_API_ERROR_EVENT,
                showErrorListener,
            );
    }, [staticInstance]);

    const defaultValue = useMemo(
        () => ({
            showError: staticInstance.error,
            showSuccess: staticInstance.success,
            showInfo: staticInstance.info,
            showWarning: staticInstance.warning,
            showNotificationByArg: staticInstance.open,
            destroy: staticInstance.destroy,
        }),
        [staticInstance],
    );

    return (
        <AntNotificationContext.Provider value={defaultValue}>
            {contextHolder}
            {children}
        </AntNotificationContext.Provider>
    );
}
